import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Banknote, CarFront, ChevronLeft, CircleDollarSign, CreditCard, PackagePlus, ReceiptText, RefreshCw, ShoppingBag, UserRound, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import WorkshopMovementForm from '../components/accounting/WorkshopMovementForm';
import SmartPosSupplierSettlement from '../components/accounting/SmartPosSupplierSettlement';
import { dateLabel, money, PAYMENT_LABELS, STATUS_LABELS, timeLabel, uniqueDocuments, vehicleLabel } from '../components/accounting/workshopFinanceModel';
import { smartPos } from '../services/smartPos';
import { hasPermission } from '../utils/permissions';
import '../components/accounting/WorkshopFinance.css';
import './SmartPOS.css';

const movementCards = [
  { kind: 'income', title: 'بيع أو إيراد مستقل', description: 'خدمة مستقلة أو إيراد غير مرتبط بمركبة أو مخزون.', action: 'تسجيل بيع', icon: ShoppingBag, tone: 'income' },
  { kind: 'expense', title: 'مصروف تشغيلي', description: 'كهرباء، عدة، إيجار أو مصروف ورشة مستقل.', action: 'تسجيل مصروف', icon: CircleDollarSign, tone: 'expense' },
  { kind: 'payroll', title: 'راتب', description: 'صرف راتب موظف مع شهر الراتب وحسابه المعتمد.', action: 'تسجيل راتب', icon: UserRound, tone: 'payroll' },
  { kind: 'supplier_settlement', title: 'تسوية مستحق مورد', description: 'سداد مستحق مستقل للمورد من نقطة البيع.', action: 'تسوية مورد', icon: Banknote, tone: 'supplier' },
  { kind: 'pos_settlement', title: 'تسوية كشف نقاط البيع', description: 'إيداع كشف البنك الفعلي: صافي البنك ورسوم القبول مقابل رصيد POS.', action: 'تسوية كشف', icon: CreditCard, tone: 'pos-settlement' },
];

const movementTitle = kind => kind === 'income' ? 'بيع أو إيراد مستقل' : kind === 'payroll' ? 'صرف راتب' : 'مصروف تشغيلي';
const movementDescription = kind => kind === 'income'
  ? 'يُنشأ الإيراد هنا للحركة المستقلة فقط. خدمة أو قطعة المركبة تُسجل من ملف الزيارة.'
  : kind === 'payroll'
    ? 'يسجل راتبًا مستقلًا من نقطة البيع بحساب الراتب وبيانات الموظف.'
    : 'يسجل مصروف الورشة المستقل. شراء المخزون أو شراء المركبة يبقى في مصدره المخصص.';
const documentKind = kind => kind === 'income' ? 'إيراد' : kind === 'payroll' ? 'راتب' : 'مصروف';

function linkageLabel(document) {
  if (document.link_type === 'customer')
    return `العميل: ${document.customer?.name || 'غير متاح'}`;
  if (document.link_type === 'vehicle')
    return `المركبة: ${vehicleLabel(document.vehicle)}`;
  return 'فوري';
}

function counterpartyLabel(document) {
  if (document.kind === 'payroll')
    return document.payee_name ? `الموظف: ${document.payee_name}` : 'الموظف غير مسجل';
  if (document.supplier?.name)
    return `المورد: ${document.supplier.name}`;
  if (document.kind === 'expense' && document.payee_name)
    return `الجهة: ${document.payee_name}`;
  return '';
}

function statusBadges(document) {
  const badges = [{ label: STATUS_LABELS[document.status] || 'غير مسجل', tone: document.status === 'reversed' ? 'reversed' : 'posted' }];
  if (document.status !== 'reversed' && document.settlement_status) {
    badges.push({ label: STATUS_LABELS[document.settlement_status] || 'غير مسجل', tone: document.settlement_status });
  }
  return badges;
}

const today = () => new Date().toISOString().slice(0, 10);
const requestId = () => {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, key => {
    const value = Math.floor(Math.random() * 16);
    return (key === 'x' ? value : ((value & 3) | 8)).toString(16);
  });
};

const asHalalas = value => {
  if (!/^\d+(?:\.\d{1,2})?$/.test(String(value || ''))) return null;
  const [whole, fraction = ''] = String(value).split('.');
  const result = Number(whole) * 100 + Number(`${fraction}00`.slice(0, 2));
  return Number.isSafeInteger(result) ? result : null;
};

function POSSettlementForm({ accounts, onClose, onSaved }) {
  const defaultFeeAccount = accounts.find(account => account.name_ar === 'رسوم قبول بطاقات')?.code || '';
  const [form, setForm] = useState({ statement_reference: '', settlement_date: today(), gross_amount: '', fee_amount: '', fee_account_code: defaultFeeAccount });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const gross = asHalalas(form.gross_amount);
  const fee = asHalalas(form.fee_amount);
  const net = gross !== null && fee !== null && fee <= gross ? ((gross - fee) / 100).toFixed(2) : '';
  const change = patch => { setError(''); setForm(previous => ({ ...previous, ...patch })); };
  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    if (!form.statement_reference.trim() || gross === null || fee === null || fee > gross || !form.fee_account_code) {
      setError('أدخل مرجع الكشف والإجمالي والرسوم الفعلية، ثم اختر حساب الرسوم.');
      return;
    }
    setBusy(true); setError('');
    try {
      const response = await smartPos.settlePos({
        provider: 'al_rajhi',
        statement_reference: form.statement_reference.trim(),
        settlement_date: form.settlement_date,
        gross_amount: form.gross_amount,
        fee_amount: form.fee_amount,
        bank_net_amount: net,
        fee_account_code: form.fee_account_code,
        request_id: requestId(),
      });
      if (!response?.data?.success || !response.data?.settlement?.id) throw new Error('تعذر تسجيل التسوية.');
      onSaved(response.data.settlement, response.data.idempotent_replay === true);
    } catch (failure) {
      setError(failure?.response?.data?.detail || failure?.message || 'تعذر تسجيل التسوية.');
    } finally { setBusy(false); }
  };
  return <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/70 p-3 sm:items-center" role="presentation">
    <section className="w-full max-w-lg rounded-2xl border border-white/15 bg-slate-900 p-5 text-slate-100 shadow-2xl" dir="rtl" role="dialog" aria-modal="true" aria-labelledby="pos-settlement-title">
      <div className="mb-4 flex items-start justify-between gap-4"><div><p className="text-xs text-cyan-200">الراجحي · كشف تسوية فعلي</p><h2 id="pos-settlement-title" className="text-lg font-bold">تسوية نقاط البيع إلى البنك</h2><p className="mt-1 text-sm text-slate-300">لا تسجّل هذه الشاشة بيعًا جديدًا. تُخفض رصيد POS بعد وصول صافي الكشف إلى البنك.</p></div><Button type="button" variant="ghost" onClick={onClose} disabled={busy}>إغلاق</Button></div>
      <form className="space-y-3" onSubmit={submit}>
        <label className="block text-sm">مرجع كشف البنك<input className="mt-1 w-full rounded-lg border border-white/20 bg-white/5 px-3 py-2" value={form.statement_reference} disabled={busy} maxLength={120} onChange={event => change({ statement_reference: event.target.value })}/></label>
        <label className="block text-sm">تاريخ وصول التسوية للبنك<input type="date" className="mt-1 w-full rounded-lg border border-white/20 bg-white/5 px-3 py-2" value={form.settlement_date} max={today()} disabled={busy} onChange={event => change({ settlement_date: event.target.value })}/></label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2"><label className="block text-sm">إجمالي POS في الكشف<input inputMode="decimal" className="mt-1 w-full rounded-lg border border-white/20 bg-white/5 px-3 py-2" value={form.gross_amount} disabled={busy} onChange={event => change({ gross_amount: event.target.value })}/></label><label className="block text-sm">الرسوم الفعلية<input inputMode="decimal" className="mt-1 w-full rounded-lg border border-white/20 bg-white/5 px-3 py-2" value={form.fee_amount} disabled={busy} onChange={event => change({ fee_amount: event.target.value })}/></label></div>
        <label className="block text-sm">صافي البنك المحسوب<input className="mt-1 w-full rounded-lg border border-emerald-300/30 bg-emerald-500/10 px-3 py-2 text-emerald-100" value={net} readOnly placeholder="الإجمالي - الرسوم"/></label>
        <label className="block text-sm">حساب رسوم قبول البطاقات<select className="mt-1 w-full rounded-lg border border-white/20 bg-slate-800 px-3 py-2" value={form.fee_account_code} disabled={busy} onChange={event => change({ fee_account_code: event.target.value })}><option value="">اختر حساب الرسوم</option>{accounts.map(account => <option key={account.id || account.code} value={account.code}>{account.code} · {account.name_ar}</option>)}</select></label>
        <p className="rounded-lg border border-amber-300/25 bg-amber-500/10 p-3 text-xs text-amber-100">القيد الناتج: مدين البنك الصافي، مدين الرسوم الفعلية، دائن POS بالإجمالي. راجع مرجع الكشف قبل الحفظ؛ لا يمكن تكراره.</p>
        {error ? <p className="rounded-lg bg-red-500/15 p-3 text-sm text-red-100" role="alert">{error}</p> : null}
        <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose} disabled={busy}>إلغاء</Button><Button type="submit" disabled={busy || !net}>{busy ? 'جارٍ الحفظ…' : 'تسجيل تسوية الكشف'}</Button></div>
      </form>
    </section>
  </div>;
}

export default function SmartPOS() {
  const navigate = useNavigate();
  const session = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('session') || '{}'); }
    catch (_) { return {}; }
  }, []);
  const [options, setOptions] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState(null);
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [posSettlementOpen, setPosSettlementOpen] = useState(false);
  const [vehicleId, setVehicleId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [optionsResponse, documentsResponse] = await Promise.all([smartPos.options(), smartPos.documents({ limit: 6 })]);
      const optionData = optionsResponse?.data;
      const documentData = documentsResponse?.data;
      if (!optionData?.success || !Array.isArray(optionData.accounts) || !Array.isArray(optionData.vehicles)
          || !documentData?.success || !Array.isArray(documentData.items)) {
        throw new Error('Invalid smart POS response');
      }
      setOptions(optionData);
      setDocuments(uniqueDocuments(documentData.items));
    } catch (_) {
      setOptions(null);
      setDocuments([]);
      setError('تعذر تحميل نقطة البيع من مصدرها. لم تُعرض بيانات بديلة.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canCreate = options?.permissions?.can_create_pos === true;
  const canPayroll = options?.permissions?.can_payroll === true;
  const canSettle = options?.permissions?.can_settle === true;
  const canPosSettlement = options?.permissions?.can_pos_settlement === true;
  const canOpenVehicle = hasPermission(session, 'vehicles', 'view') || hasPermission(session, 'archive', 'view');
  const canOpenInventory = hasPermission(session, 'inventory', 'view');
  const selectedVehicle = useMemo(
    () => (options?.vehicles || []).find(vehicle => String(vehicle.id) === String(vehicleId)) || null,
    [options?.vehicles, vehicleId],
  );
  const visibleCards = movementCards.filter(card => card.kind !== 'payroll' || canPayroll);
  const openVehicle = () => {
    if (!selectedVehicle?.id) {
      setNotice('اختر مركبة لفتح ملفها؛ البنود والدفعات المرتبطة بالمركبة تُسجل من الزيارة.');
      return;
    }
    navigate(`/vehicle/${encodeURIComponent(selectedVehicle.id)}`);
  };
  const saved = document => {
    setForm(null);
    setNotice(`تم تسجيل ${movementTitle(document?.kind || form?.kind)} من نقطة البيع وتحديث السجل.`);
    load();
  };
  const settlementSaved = () => {
    setSettlementOpen(false);
    setNotice('تم تسجيل سداد مستحق المورد وتحديث السجل.');
    load();
  };
  const posSettlementSaved = (settlement, replayed) => {
    setPosSettlementOpen(false);
    setNotice(replayed ? `هذا الكشف مسجل سابقًا: ${settlement.statement_reference}.` : `تمت تسوية كشف POS «${settlement.statement_reference}» إلى البنك.`);
    load();
  };

  return <main className="smart-pos-page" dir="rtl" data-testid="smart-pos-page">
    <header className="smart-pos-heading">
      <div>
        <p className="smart-pos-eyebrow"><CreditCard size={15} /> نقطة البيع</p>
        <h1>نقطة البيع الذكية</h1>
        <p>تسجل الحركات المستقلة من مصدر POS، وتحوّل حركات المركبة إلى ملف الزيارة حتى لا يتكرر الإيراد أو المخزون.</p>
      </div>
      <Button type="button" variant="outline" onClick={load} disabled={loading} aria-label="تحديث نقطة البيع">
        <RefreshCw size={17} className={loading ? 'animate-spin' : ''} />تحديث
      </Button>
    </header>

    {notice ? <p className="wms-finance-notice" role="status">{notice}</p> : null}
    {error ? <div className="wms-finance-error" role="alert">{error}<Button type="button" variant="outline" onClick={load}>إعادة المحاولة</Button></div> : null}
    {!error && <>
      <section className="smart-pos-actions" aria-label="حركات نقطة البيع">
        {visibleCards.map(card => {
          const Icon = card.icon;
          const allowed = card.kind === 'supplier_settlement' ? canSettle : card.kind === 'pos_settlement' ? canPosSettlement : canCreate;
          return <Card className={`smart-pos-action-card smart-pos-action-${card.tone}`} key={card.kind}>
            <CardHeader>
              <span className="smart-pos-icon" aria-hidden="true"><Icon size={22} /></span>
              <CardTitle className="smart-pos-card-title">{card.title}</CardTitle>
              <CardDescription className="smart-pos-card-description">{card.description}</CardDescription>
            </CardHeader>
            <CardContent className="smart-pos-card-content">
              <Button type="button" onClick={() => card.kind === 'supplier_settlement' ? setSettlementOpen(true) : card.kind === 'pos_settlement' ? setPosSettlementOpen(true) : setForm({ kind: card.kind })} disabled={!allowed}><ReceiptText size={16} />{card.action}</Button>
              {!allowed ? <small>{card.kind === 'supplier_settlement' ? 'صلاحية التسوية غير متاحة لهذا المستخدم.' : card.kind === 'pos_settlement' ? 'تسوية كشف POS مخصصة لمدير النظام.' : 'صلاحية التسجيل غير متاحة لهذا المستخدم.'}</small> : null}
            </CardContent>
          </Card>;
        })}
      </section>

      {canOpenVehicle ? <section className="smart-pos-vehicle-section" aria-label="مسارات المركبة">
        <div>
          <p className="smart-pos-eyebrow"><CarFront size={15} /> المركبات</p>
          <h2>خدمة، قطع، تحصيل أو شراء للمركبة</h2>
          <p>هذه الحركات لا تُكتب كبيع مستقل. افتح ملف المركبة لإضافة بنود الزيارة، اعتماد الإجمالي أو تسجيل دفعة الزيارة من مصدرها القانوني.</p>
        </div>
        <div className="smart-pos-vehicle-picker">
          <label htmlFor="smart-pos-vehicle">المركبة</label>
          <select id="smart-pos-vehicle" value={vehicleId} onChange={event => { setVehicleId(event.target.value); setNotice(''); }} disabled={loading || !(options?.vehicles || []).length}>
            <option value="">اختر مركبة</option>
            {(options?.vehicles || []).map(vehicle => <option key={vehicle.id} value={vehicle.id}>{vehicleLabel(vehicle)}</option>)}
          </select>
          <Button type="button" variant="outline" onClick={openVehicle} disabled={loading}><Wrench size={16} />فتح ملف المركبة</Button>
        </div>
      </section> : null}

      <section className="smart-pos-links" aria-label="مسارات مرتبطة">
        <Card>
          <CardHeader><span className="smart-pos-icon smart-pos-icon-blue"><ReceiptText size={21} /></span><CardTitle className="smart-pos-card-title">المستندات المستقلة</CardTitle><CardDescription className="smart-pos-card-description">عرض المستند أو عكسه من سجله. تسوية المورد المستقل متاحة من بطاقة التسوية أعلاه.</CardDescription></CardHeader>
          <CardContent><Button asChild variant="outline"><Link to="/accounting/journal-entries?view=documents&outstanding_only=true">فتح المستندات المستحقة <ChevronLeft size={16} /></Link></Button></CardContent>
        </Card>
        {canOpenInventory ? <Card>
          <CardHeader><span className="smart-pos-icon smart-pos-icon-violet"><PackagePlus size={21} /></span><CardTitle className="smart-pos-card-title">إدارة المخزون</CardTitle><CardDescription className="smart-pos-card-description">شراء المخزون أو شراء المركبة يبقى في مصدره المخصص ولا يتحول تلقائيًا إلى مصروف تشغيلي مستقل.</CardDescription></CardHeader>
          <CardContent><Button asChild variant="outline"><Link to="/parts">فتح المخزون <ChevronLeft size={16} /></Link></Button></CardContent>
        </Card> : null}
        <Card>
          <CardHeader><span className="smart-pos-icon smart-pos-icon-amber"><Banknote size={21} /></span><CardTitle className="smart-pos-card-title">إيداع نقدي في البنك</CardTitle><CardDescription className="smart-pos-card-description">غير متاح حاليًا لأن التحويل بين الأصول يحتاج مصدرًا قانونيًا مستقلًا، ولا يمثل إيرادًا أو مصروفًا.</CardDescription></CardHeader>
          <CardContent><span className="smart-pos-unavailable">قيد الإعداد المحاسبي</span></CardContent>
        </Card>
      </section>

      <section className="smart-pos-recent" aria-label="آخر حركات نقطة البيع">
        <div className="smart-pos-recent-heading">
          <div><p className="smart-pos-eyebrow"><ReceiptText size={15} /> السجل</p><h2>آخر حركات نقطة البيع</h2></div>
          <Link to="/accounting/journal-entries">دفتر اليومية</Link>
        </div>
        {loading ? <p className="wms-finance-state" role="status">جارٍ تحميل الحركات…</p> : !documents.length ? <p className="wms-finance-state">لا توجد حركة مسجلة من نقطة البيع بعد.</p> : <div className="smart-pos-recent-grid">
          {documents.map(document => <Card key={document.id} className={`smart-pos-recent-card smart-pos-recent-${document.kind}`}>
            <CardHeader>
              <div className="smart-pos-recent-top">
                <div><strong>{documentKind(document.kind)}</strong><span className="smart-pos-document-number">{document.number || 'رقم غير مسجل'}</span></div>
                <bdi>{money(document.amount)}</bdi>
              </div>
              <CardTitle className="smart-pos-card-title">{document.description || 'وصف غير مسجل'}</CardTitle>
              <CardDescription>{dateLabel(document.entry_date)}{timeLabel(document.created_at) ? ` · ${timeLabel(document.created_at)}` : ''}</CardDescription>
            </CardHeader>
            <CardContent className="smart-pos-recent-content">
              <div className="smart-pos-statuses" aria-label="حالة المستند">
                {statusBadges(document).map((badge, index) => <span className={`smart-pos-status smart-pos-status-${badge.tone}`} key={`${badge.label}-${index}`}>{badge.label}</span>)}
              </div>
              <dl className="smart-pos-recent-details">
                <div><dt>الربط</dt><dd>{linkageLabel(document)}</dd></div>
                {counterpartyLabel(document) ? <div><dt>الطرف</dt><dd>{counterpartyLabel(document)}</dd></div> : null}
                <div><dt>وسيلة السداد</dt><dd>{PAYMENT_LABELS[document.payment_method] || 'غير مسجلة'}</dd></div>
                <div><dt>المنشأ</dt><dd>نقطة بيع</dd></div>
                <div><dt>المستخدم</dt><dd>{document.actor_name || 'غير مسجل'}</dd></div>
              </dl>
              <Link className="smart-pos-document-link" to={`/accounting/journal-entries?view=documents&document=${encodeURIComponent(document.id)}`}>فتح المستند <ChevronLeft size={15} /></Link>
            </CardContent>
          </Card>)}
        </div>}
      </section>
    </>}

    {form ? <WorkshopMovementForm key={form.kind} defaults={{ kind: form.kind, link_type: 'instant' }} allowedKinds={[form.kind]} allowedLinkTypes={['instant', 'customer']} movementService={smartPos} dialogTitle={movementTitle(form.kind)} dialogDescription={movementDescription(form.kind)} submitLabel="تسجيل من نقطة البيع" onClose={() => setForm(null)} onSaved={saved} /> : null}
    {settlementOpen ? <SmartPosSupplierSettlement onClose={() => setSettlementOpen(false)} onSaved={settlementSaved} /> : null}
    {posSettlementOpen ? <POSSettlementForm accounts={options?.pos_fee_accounts || []} onClose={() => setPosSettlementOpen(false)} onSaved={posSettlementSaved} /> : null}
  </main>;
}
